import React, { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, Building2, Eye, EyeOff, Loader2, Mail, Phone, Sparkles, Ticket, User } from 'lucide-react';

import { authService } from '../../services/authService';
import { getErrorMessage } from '../../utils/error';
import { useLanguage } from '../../features/language/useLanguage';

const buildRegisterSchema = (t: (key: string, params?: Record<string, string | number>, fallback?: string) => string) =>
  z
    .object({
      companyName: z.string().trim().min(2, t('auth.validation.companyNameShort')),
      fullName: z.string().trim().min(2, t('auth.validation.fullNameRequired')),
      email: z.string().trim().email(t('auth.validation.invalidEmail')),
      phone: z.string().trim().min(4, t('auth.validation.phoneMinLength')),
      password: z
        .string()
        .min(6, t('auth.validation.passwordMinLength'))
        .regex(/[A-Z]/, t('auth.validation.passwordUppercase')),
      confirmPassword: z.string(),
      voucherCode: z.string().trim().optional(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth.validation.passwordMismatch'),
      path: ['confirmPassword'],
    });

type RegisterFormData = z.infer<ReturnType<typeof buildRegisterSchema>>;

const computeStrength = (password: string): number => {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 6) score += 1;
  if (password.length >= 10) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  return Math.min(score, 4);
};

const STRENGTH_COLORS = ['bg-gray-200', 'bg-red-400', 'bg-amber-400', 'bg-lime-500', 'bg-emerald-500'];
const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Good', 'Strong'];

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useLanguage();
  const registerSchema = useMemo(() => buildRegisterSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
  });

  const passwordValue = useWatch({ control, name: 'password' }) ?? '';
  const strength = computeStrength(passwordValue);

  // Phone is a required Indonesian number. A fixed +62 prefix is shown beside the
  // field, so the input holds national digits only: strip anything non-numeric and a
  // leading zero (0812… → 812…), and cap at 12 digits so the stored +62 number stays
  // within the 15-character E.164 maximum. Since only digits survive the strip, the
  // schema's 4-character minimum is effectively a 4-digit minimum.
  const phoneField = register('phone');
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 12);
    phoneField.onChange(e);
  };

  const onSubmit = async (data: RegisterFormData) => {
    const voucherCode = data.voucherCode?.trim() || '';

    // Validate voucher BEFORE the OTP step so a bad code surfaces here on the
    // voucher field, not after the user has already verified their email.
    if (voucherCode) {
      try {
        await authService.validateVoucher(voucherCode);
      } catch (error) {
        const apiError = (error as { response?: { data?: { error?: string } } })?.response?.data;
        const code = apiError?.error;
        let message = t('auth.register.voucherInvalid');
        switch (code) {
          case 'voucher.not_found':
            message = t('auth.register.voucherNotFound');
            break;
          case 'voucher.expired':
            message = t('auth.register.voucherExpired');
            break;
          case 'voucher.exhausted':
            message = t('auth.register.voucherExhausted');
            break;
        }
        setError('voucherCode', { message });
        return;
      }
    }

    try {
      await authService.checkEmail(data.email);
      await authService.requestOtp(data.email);

      navigate('/verify-otp', {
        state: {
          registrationData: {
            company_name: data.companyName,
            full_name: data.fullName,
            email: data.email,
            phone: `+62${data.phone.trim()}`,
            password: data.password,
            voucher_code: voucherCode || undefined,
          },
        },
      });
    } catch (error) {
      // Duplicate email: backend's check-email returns 409 with
      // { available: false, message: "Email already registered" }. Pin to the
      // status code so we don't depend on a substring match in the message.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setError('email', { message: t('auth.validation.emailAlreadyInUse') });
        return;
      }
      const message = getErrorMessage(error);
      setError('root', { message });
    }
  };

  const textFields: Array<{
    id: keyof RegisterFormData;
    label: string;
    type: string;
    placeholder: string;
    autoComplete: string;
    Icon: React.ComponentType<{ size?: number; className?: string }>;
  }> = [
    {
      id: 'companyName',
      label: t('auth.register.companyName'),
      type: 'text',
      placeholder: t('auth.register.companyNamePlaceholder'),
      autoComplete: 'organization',
      Icon: Building2,
    },
    {
      id: 'fullName',
      label: t('auth.register.fullName'),
      type: 'text',
      placeholder: t('auth.register.fullNamePlaceholder'),
      autoComplete: 'name',
      Icon: User,
    },
    {
      id: 'phone',
      label: t('auth.register.phoneNumber'),
      type: 'tel',
      placeholder: t('auth.register.phoneNumberPlaceholder'),
      autoComplete: 'tel',
      Icon: Phone,
    },
    {
      id: 'email',
      label: t('auth.register.emailAddress'),
      type: 'email',
      placeholder: t('auth.register.emailPlaceholder'),
      autoComplete: 'email',
      Icon: Mail,
    },
  ];

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-6">
        <h2 className="text-[28px] leading-tight font-semibold tracking-tight text-gray-900">
          {t('auth.register.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          {t('auth.register.alreadyHaveCompany')}{' '}
          <Link
            to="/login"
            className="font-medium text-primary-600 underline-offset-4 hover:underline transition-colors"
          >
            {t('auth.register.signInHere')}
          </Link>
        </p>
      </div>

      <div className="mb-6 flex items-start gap-3 rounded-xl border border-primary-200 bg-gradient-to-br from-primary-50 via-primary-50/60 to-primary-100/50 px-3.5 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white shadow-sm shadow-primary-500/30">
          <Sparkles size={15} />
        </span>
        <div className="leading-snug">
          <p className="text-sm font-semibold text-primary-900">
            {t('auth.register.trialBadgeTitle')}
          </p>
          <p className="mt-0.5 text-xs text-primary-700/80">
            {t('auth.register.trialBadgeSubtitle')}
          </p>
        </div>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
        {errors.root && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50/80 px-3.5 py-3 text-sm text-red-700"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-500" />
            <span className="leading-relaxed">{errors.root.message}</span>
          </div>
        )}

        {textFields.map(({ id, label, type, placeholder, autoComplete, Icon }) => {
          const fieldError = errors[id];
          return (
            <div key={id} className="space-y-1.5">
              <label htmlFor={id} className="block text-xs font-medium text-gray-700 tracking-wide">
                {label}
              </label>
              {id === 'phone' ? (
                <div
                  className={`flex items-stretch overflow-hidden rounded-lg border bg-white shadow-sm transition-all focus-within:ring-4 ${
                    fieldError
                      ? 'border-red-400 focus-within:border-red-500 focus-within:ring-red-500/10'
                      : 'border-gray-200 focus-within:border-primary-500 focus-within:ring-primary-500/10'
                  }`}
                >
                  <span className="flex shrink-0 select-none items-center gap-2 pl-3 pr-2.5 text-sm font-medium text-gray-600">
                    <Icon size={16} className={fieldError ? 'text-red-400' : 'text-gray-400'} />
                    +62
                  </span>
                  <span
                    aria-hidden
                    className={`my-2 w-px ${fieldError ? 'bg-red-200' : 'bg-gray-200'}`}
                  />
                  <input
                    {...phoneField}
                    onChange={handlePhoneChange}
                    id={id}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    maxLength={12}
                    placeholder={placeholder}
                    className="block w-full border-0 bg-transparent py-2.5 pl-2.5 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                  />
                </div>
              ) : (
                <div className="relative group">
                  <Icon
                    size={16}
                    className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${
                      fieldError ? 'text-red-400' : 'text-gray-400 group-focus-within:text-primary-600'
                    }`}
                  />
                  <input
                    {...register(id)}
                    id={id}
                    type={type}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    className={`block w-full rounded-lg border bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-all focus:outline-none focus:ring-4 ${
                      fieldError
                        ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10'
                        : 'border-gray-200 focus:border-primary-500 focus:ring-primary-500/10'
                    }`}
                  />
                </div>
              )}
              {fieldError && <p className="text-xs text-red-600">{fieldError.message}</p>}
            </div>
          );
        })}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {(['password', 'confirmPassword'] as const).map((id) => {
            const fieldError = errors[id];
            const isConfirm = id === 'confirmPassword';
            return (
              <div key={id} className="space-y-1.5">
                <label htmlFor={id} className="block text-xs font-medium text-gray-700 tracking-wide">
                  {isConfirm ? t('auth.register.confirm') : t('auth.register.password')}
                </label>
                <div className="relative">
                  <input
                    {...register(id, !isConfirm ? { deps: ['confirmPassword'] } : {})}
                    id={id}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={isConfirm ? 'new-password' : 'new-password'}
                    className={`block w-full rounded-lg border bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-all focus:outline-none focus:ring-4 ${
                      fieldError
                        ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10'
                        : 'border-gray-200 focus:border-primary-500 focus:ring-primary-500/10'
                    }`}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {fieldError && <p className="text-xs text-red-600">{fieldError.message}</p>}
              </div>
            );
          })}
        </div>

        {passwordValue && (
          <div className="pt-1">
            <div className="flex items-center gap-1.5" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                    i < strength ? STRENGTH_COLORS[strength] : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="mt-1.5 text-[11px] font-medium tracking-wide text-gray-500">
              {STRENGTH_LABELS[strength]}
            </p>
          </div>
        )}

        <div className="space-y-1.5 pt-1">
          <label
            htmlFor="voucherCode"
            className="block text-xs font-medium text-gray-700 tracking-wide"
          >
            {t('auth.register.voucherCodeOptional')}
          </label>
          <div className="relative group">
            <Ticket
              size={16}
              className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${
                errors.voucherCode ? 'text-red-400' : 'text-gray-400 group-focus-within:text-primary-600'
              }`}
            />
            <input
              {...register('voucherCode')}
              id="voucherCode"
              type="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder={t('auth.register.voucherCodePlaceholder')}
              className={`block w-full rounded-lg border bg-white pl-9 pr-3 py-2.5 text-sm font-mono uppercase tracking-wider text-gray-900 placeholder:font-sans placeholder:normal-case placeholder:tracking-normal placeholder:text-gray-400 shadow-sm transition-all focus:outline-none focus:ring-4 ${
                errors.voucherCode
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10'
                  : 'border-gray-200 focus:border-primary-500 focus:ring-primary-500/10'
              }`}
            />
          </div>
          {errors.voucherCode ? (
            <p className="text-xs text-red-600">{errors.voucherCode.message}</p>
          ) : (
            <p className="text-[11px] text-gray-500">{t('auth.register.voucherCodeHint')}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-primary-500 to-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_1px_2px_0_rgba(2,132,199,0.4)] ring-1 ring-primary-700/20 transition-all hover:from-primary-500 hover:to-primary-700 hover:shadow-[0_1px_0_0_rgba(255,255,255,0.2)_inset,0_4px_12px_-2px_rgba(2,132,199,0.5)] focus:outline-none focus:ring-4 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <span>{t('auth.register.next')}</span>
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </form>
    </div>
  );
};
